import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { NotificationAccountContact } from '@models/notifications';
import { PusherService } from './pusher.service';

const NOTIFICATIONS_PUSHER_EVENT_NAME = 'notification';
const NOTIFICATIONS_CACHE_KEY = 'notifications_cache';
const NOTIFICATIONS_CACHE_TIMESTAMP_KEY = 'notifications_cache_timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly pusherService = inject(PusherService);

  pusherChannelSubscription: Subscription;
  onNotificationsUpdated: BehaviorSubject<NotificationAccountContact[]> = new BehaviorSubject([]);
  notifications: NotificationAccountContact[] = [];

  async getNotifications(forceReload = false): Promise<NotificationAccountContact[]> {
    try {
      if (!forceReload && this.isCacheValid()) {
        const cachedNotifications = this.getNotificationsFromCache();
        if (cachedNotifications) {
          this.notifications = cachedNotifications;
          this.onNotificationsUpdated.next(this.notifications);

          return this.notifications;
        }
      }

      this.notifications = await firstValueFrom(
        this.http.get<NotificationAccountContact[]>(`${import.meta.env.NG_APP_PRIVATE_BACK_URL}getNotifications`)
      );

      this.saveNotificationsToCache(this.notifications);
      this.onNotificationsUpdated.next(this.notifications);

      return this.notifications;
    } catch {
      return [];
    }
  }

  private isCacheValid(): boolean {
    const timestamp = localStorage.getItem(NOTIFICATIONS_CACHE_TIMESTAMP_KEY);

    if (!timestamp) return false;

    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();

    return now - cacheTime < CACHE_DURATION_MS;
  }

  private getNotificationsFromCache(): NotificationAccountContact[] | null {
    const cached = localStorage.getItem(NOTIFICATIONS_CACHE_KEY);
    if (!cached) return null;

    return JSON.parse(cached);
  }

  private saveNotificationsToCache(notifications: NotificationAccountContact[]): void {
    localStorage.setItem(NOTIFICATIONS_CACHE_KEY, JSON.stringify(notifications));
    localStorage.setItem(NOTIFICATIONS_CACHE_TIMESTAMP_KEY, Date.now().toString());
  }

  clearNotificationsCache(): void {
    localStorage.removeItem(NOTIFICATIONS_CACHE_KEY);
    localStorage.removeItem(NOTIFICATIONS_CACHE_TIMESTAMP_KEY);
  }

  async subscribeToNotificationsPusherChannel() {
    if (this.pusherChannelSubscription) return;
    const subscription = await this.pusherService.subscribeEvent<NotificationAccountContact>(NOTIFICATIONS_PUSHER_EVENT_NAME, false);
    subscription.subscribe((newNotification: NotificationAccountContact) => {
      if (!newNotification) return;
      this.notifications.unshift(newNotification);
      this.saveNotificationsToCache(this.notifications);
      this.onNotificationsUpdated.next(this.notifications);
    });
  }

  async setAllNotificationAsViewed() {
    try {
      this.notifications.forEach(notification => (notification.viewed_at = new Date().toISOString()));
      await firstValueFrom(this.http.post(`${import.meta.env.NG_APP_PRIVATE_BACK_URL}setAllNotificationAsViewed`, {}));
    } catch (error) {
      console.error(error);
    }
  }

  async setNotificationAsOpened(notification_id: number) {
    try {
      await firstValueFrom(this.http.post(`${import.meta.env.NG_APP_PRIVATE_BACK_URL}setNotificationAsOpened`, { notification_id }));
    } catch (error) {
      console.error(error);
    }
  }

  unsubscribeToNotificationsPusherChannel() {
    this.pusherChannelSubscription?.unsubscribe();
    this.pusherChannelSubscription = null;
  }
}
